import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { TrainingPartner, Training } from '@/types/trainings';
import { useToast } from '@/shared/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface UseTrainingPartnersProps {
  training?: Training | null;
  trainingId?: string;
}

export const useTrainingPartners = (props: UseTrainingPartnersProps | null) => {
  const { training, trainingId } = props || {};
  const { user } = useAuth();
  const { toast } = useToast();
  const [partners, setPartners] = useState<TrainingPartner[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get the actual training ID
  const actualTrainingId = training?.id || trainingId;

  // Load partners when training changes
  useEffect(() => {
    const fetchPartners = async () => {
      if (!actualTrainingId || !user) {
        setPartners([]);
        return;
      }
      
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        const { getEdgeFunctionUrl } = await import('@/utils/url');
        const functionUrl = getEdgeFunctionUrl('get_training_partners');

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ training_id: actualTrainingId })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('🔄 useTrainingPartners - Response error:', errorText);
          throw new Error('Failed to fetch partners');
        }
        
        const result = await response.json();
        setPartners(result.data || []);
      } catch (error) {
        console.error('🔄 useTrainingPartners - Error fetching partners:', error);
        // Only show toast if we don't have fallback data
        // The TrainingViewModal handles its own fallback, so we don't need to show error here
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, [actualTrainingId, user, toast]);

  // Load partners from training data (this will be populated from the training object)
  const loadPartners = useCallback((trainingPartners: TrainingPartner[] = []) => {
    setPartners(trainingPartners);
  }, []);

  // Add partner to training
  const addPartner = useCallback(async (partner: TrainingPartner) => {
    if (!user || !actualTrainingId) {
      return;
    }

    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const { getEdgeFunctionUrl } = await import('@/utils/url');
      const functionUrl = getEdgeFunctionUrl('link_training_partners');

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          trainingId: actualTrainingId,
          action: 'add',
          partnerData: {
            partner_user_id: partner.partner_user_id,
            manual_partner_name: partner.manual_partner_name
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔄 useTrainingPartners - Response error:', errorText);
        throw new Error('Failed to add partner');
      }

      const result = await response.json();
      setPartners(prev => [...prev, { ...partner, id: result.data.id }]);

      toast({
        title: t('notifications.toasts.partner_added'),
        description: t('notifications.toasts.partner_added_description'),
      });

      return result.data;
    } catch (error) {
      console.error('🔄 useTrainingPartners - Error adding partner:', error);
      toast({
        title: t('notifications.toasts.error_adding_partner'),
        description: t('notifications.toasts.error_adding_partner_description'),
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [user, actualTrainingId, toast]);

  // Remove partner from training
  const removePartner = useCallback(async (partnerId: string) => {
    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('No access token');
      }

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const requestBody = {
        trainingId: actualTrainingId,
        action: 'remove',
        partnerData: {
          partner_id: partnerId
        }
      };

      // Remove the specific partner from database
      const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error removing partner:', errorText);
        throw new Error(`Failed to remove partner: ${errorText}`);
      }

      const result = await response.json();

      // Update local state
      setPartners(prev => {
        const newPartners = prev.filter(p => p.id !== partnerId);
        return newPartners;
      });
    } catch (error) {
      console.error('Error removing partner:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [actualTrainingId]);

  // Save all partners at once (for batch operations)
  const savePartners = useCallback(async (partnersToSave: TrainingPartner[]) => {
    if (!user || !actualTrainingId) return [];

    setSaving(true);
    try {
      // First, remove all existing partners
      for (const partner of partners) {
        await removePartner(partner.id);
      }

      // Then add all new partners and collect the results
      const savedPartners = [];
      for (const partner of partnersToSave) {
        const savedPartner = await addPartner(partner);
        savedPartners.push(savedPartner);
      }

      toast({
        title: t('notifications.toasts.partners_saved'),
        description: t('notifications.toasts.partners_saved_description'),
      });

      return savedPartners;
    } catch (error) {
      console.error('Error saving partners:', error);
      toast({
        title: t('notifications.toasts.error_saving_partners'),
        description: t('notifications.toasts.error_saving_partners_description'),
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [user, actualTrainingId, partners, removePartner, addPartner, toast]);

  return {
    partners,
    loading,
    saving,
    loadPartners,
    addPartner,
    removePartner,
    savePartners,
  };
}; 

// Hook for using pre-loaded partners data
export const useTrainingPartnersFromData = (trainingId: string, partnersData?: any[], refreshKey?: number) => {
  const [partners, setPartners] = useState<TrainingPartner[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const { session } = useAuth();

  // Load partners from API if no data is provided
  const loadPartnersFromAPI = useCallback(async () => {
    if (!trainingId) {
      return;
    }
    
    setLoading(true);
    try {
      const token = session?.access_token;

      if (!token) {
        return;
      }

      const { getEdgeFunctionUrl } = await import('@/utils/url');
      const functionUrl = getEdgeFunctionUrl('get_training_partners');

      const response = await fetch(`${functionUrl}?t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ training_id: trainingId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error loading partners from API:', errorText);
        return;
      }

      const result = await response.json();
      setPartners(result.data || []);
    } catch (error) {
      console.error('Error loading partners from API:', error);
    } finally {
      setLoading(false);
    }
  }, [trainingId, session]);

  // Initialize partners from data or load from API
  useEffect(() => {
    if (partnersData && Array.isArray(partnersData) && partnersData.length > 0) {
      // Use provided data
      const dataString = JSON.stringify(partnersData);
      const partnersString = JSON.stringify(partners);
      if (dataString !== partnersString) {
        setPartners(partnersData);
      }
      setLoading(false);
    } else {
      // Load from API if no data provided
      loadPartnersFromAPI();
    }
  }, [trainingId, JSON.stringify(partnersData), loadPartnersFromAPI]);

  // Reload partners when refreshKey changes
  useEffect(() => {
    if (refreshKey && refreshKey > 0 && trainingId) {
      setTimeout(() => {
        loadPartnersFromAPI();
      }, 100);
    }
  }, [refreshKey, trainingId, loadPartnersFromAPI]);

  // Load partners from training data (this will be populated from the training object)
  const loadPartners = useCallback((trainingPartners: TrainingPartner[] = []) => {
    setPartners(trainingPartners);
    setHasLocalChanges(false);
  }, []);

  // Add partner to training (still needs individual request for updates)
  const addPartner = useCallback(async (partner: TrainingPartner) => {
    setPartners(prev => [...prev, partner]);
    setHasLocalChanges(true);
  }, []);

  // Remove partner from training
  const removePartner = useCallback(async (partnerId: string) => {
    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('No access token');
      }

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const requestBody = {
        trainingId: trainingId,
        action: 'remove',
        partnerData: {
          partner_id: partnerId
        }
      };
      
      // Remove the specific partner from database
      const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error removing partner:', errorText);
        throw new Error(`Failed to remove partner: ${errorText}`);
      }

      const result = await response.json();

      // Update local state
      setPartners(prev => {
        const newPartners = prev.filter(p => p.id !== partnerId);
        return newPartners;
      });
    } catch (error) {
      console.error('Error removing partner:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [trainingId]);

  // Save partners (for backward compatibility)
  const savePartners = useCallback(async (partnersToSave: TrainingPartner[]) => {
    if (!partnersToSave || partnersToSave.length === 0) {
      setPartners([]);
      setHasLocalChanges(false);
      return [];
    }

    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('No access token');
      }

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      // First, remove all existing partners
      for (const partner of partners) {
        if (partner.id) {
          const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              trainingId: trainingId,
              action: 'remove',
              partnerData: {
                partner_id: partner.id
              }
            })
          });

          if (!response.ok) {
            console.error('Error removing partner:', await response.text());
          }
        }
      }

      // Then add all new partners and collect the results
      const savedPartners = [];
      for (const partner of partnersToSave) {
        // Check if partner already exists in the new list (partnersToSave)
        const partnerExistsInNewList = partnersToSave.filter(p => 
          (p.partner_user_id && p.partner_user_id === partner.partner_user_id) ||
          (p.manual_partner_name && p.manual_partner_name === partner.manual_partner_name)
        ).length > 1;
        
        if (partnerExistsInNewList) {
          continue;
        }
        
        const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            trainingId: trainingId,
            action: 'add',
            partnerData: {
              partner_user_id: partner.partner_user_id,
              manual_partner_name: partner.manual_partner_name
            }
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error adding partner:', errorText);
          
          // If it's a 409 conflict, it means the partner already exists in database, so we can ignore this error
          if (response.status === 409) {
            savedPartners.push(partner); // Add the existing partner to results
          } else {
            throw new Error(`Failed to add partner: ${errorText}`);
          }
        } else {
          const result = await response.json();
          savedPartners.push({ ...partner, id: result.data.id });
        }
      }

      setPartners(partnersToSave);
      setHasLocalChanges(false);
      return savedPartners;
    } catch (error) {
      console.error('Error saving partners:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [trainingId, partners]);

  return {
    partners,
    loading,
    saving,
    addPartner,
    removePartner,
    savePartners,
    loadPartners
  };
}; 